import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Receipt, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

/**
 * Réclame le régime TVA de l'organisation tant qu'il n'est pas déclaré.
 *
 * Pourquoi une bannière et non un verrou : LogFit émettra les documents de
 * vente AU NOM de l'organisateur, et une ASBL exonérée (art. 44) ne facture
 * pas comme une SRL. Sans son régime, aucun document correct n'est produisible.
 * Mais l'exiger tout de suite bloquerait des organisateurs qui n'ont encore
 * rien à facturer — d'où un rappel permanent plutôt qu'une porte fermée.
 *
 * Le verrou dur (`require_vat_regime`, 403) n'entre en jeu qu'au moment de la
 * première action facturable, et seulement quand BILLING_ENABLED est activé.
 */
const VatRegimeBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, vatRegimeDeclared } = useAuth();

  // `null` = pas encore chargé, ou features indisponibles : on ne réclame rien
  // sur une supposition.
  if (vatRegimeDeclared !== false) return null;
  // Un participant n'a pas d'organisation, un admin LogFit n'a pas à déclarer
  // le régime de quelqu'un d'autre depuis cet écran.
  if (user?.role !== 'organizer') return null;

  return (
    <div className="border-b border-warning/30 bg-warning/10">
      <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6">
        <div className="flex items-start gap-3">
          <Receipt className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('vatBanner.title')}</p>
            <p className="text-xs text-muted-foreground">{t('vatBanner.description')}</p>
          </div>
        </div>
        <Button
          size="sm"
          className="shrink-0 self-start sm:self-auto"
          onClick={() => navigate('/admin/settings?tab=vat')}
        >
          {t('vatBanner.action')}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default VatRegimeBanner;
